// Client-side pre-validation for the Item form. Runs on PUBLISH (not Save
// Draft — PRD §3.4 keeps drafts lenient). The BE remains authoritative; this
// just lets the form surface inline errors before paying the round-trip and
// matches PRD §8 wording.
//
// Field keys mirror the dotted paths BE returns in `error.fields`, so the
// same `fieldErrors` map can be hydrated from either client or server.

import type {
  ProItemScope,
  ProMeasurementCycle,
  DayOfWeek,
  FirstExecution,
  FirstExecutionKind,
} from '../types/pro-item-design.types';

export interface ValidatableForm {
  name: string;
  scope: ProItemScope;
  diseaseGroupId: number | null;
  diseaseCodeId: number | null;
  cycle: ProMeasurementCycle;
  daysOfWeek: DayOfWeek[];
  firstExecutionKind: FirstExecutionKind;
  firstExecutionNDays: number;
  // COMPLEX mode was removed per ADM.036 (SHCT-391) — SUM ranges only.
  gradeSumG1Max: number;
  gradeSumG2Max: number;
}

export type FieldErrors = Record<string, string>;

// Each leaf returns the i18n key suffix under `item.validation.*` — the caller
// resolves the actual user-facing string with `t(...)`. Keeping it as a key
// (rather than a message) means the validator stays pure (no React deps).
export type ValidationKey =
  | 'nameRequired'
  | 'nameTooLong'
  | 'diseaseGroupRequired'
  | 'diseaseCodeRequired'
  | 'daysRequired'
  | 'nDaysRange'
  | 'gradeRangeOverlap'
  | 'gradeRangeMissing';

export type ValidationKeys = Record<string, ValidationKey>;

/**
 * SHCT-609 — 요일 is required by the **측정 주기**, never by the 첫 시행 기준.
 *
 * PRD 04 v1.22 §3.2 states the two are separate axes ([ADM.055]: 첫 시행 기준은
 * 시작 오프셋, 측정 주기는 반복 간격), and its 주기 환산 table assigns 요일 적용 per
 * cycle: 전 요일 for `매일`, 지정 요일 for `주 1회`·`격주`·`월 1회`, 미적용 for
 * `분기별` ([ADM.056] — the execution date is 첫 실행일 + 90일 × k and ignores the
 * weekday). §4.2 and §8.1 both exempt **분기별 only**.
 *
 * That exemption is what the 발주처 schedule sheet needs:
 * `docs/guides/PRO_schedule_reference.xlsx [측정주기]` item 17 (보호자의 질병 이해도)
 * is 1분기 · 퇴원후 90일 with the weekday column deliberately blank — a 분기별 row.
 *
 * An earlier build keyed this off `firstExecutionKind === 'WEEK_AFTER_DISCHARGE'`
 * instead, which exempted EVERY cycle whenever the anchor was 퇴원 후 다음날 /
 * 퇴원 후 N일. That is not a copy mismatch: the server's `ProScheduleResolver`
 * routes 주 1회·격주·월 1회 through a weekday gate that returns false for an empty
 * `daysOfWeek`, and this screen disables the picker when 요일 is not required — so
 * `주 1회` + `퇴원 후 N일` saved an item that was **never due for any patient**,
 * silently, and did not even register as a delayed response.
 */
/**
 * PRD 04 §3.2 「첫 시행 기준 = 주기별 자동 결정」 (SHCT-776) — the first-execution criteria is
 * DERIVED from the measurement cycle, never chosen by the administrator.
 *
 * `WEEK_AFTER_DISCHARGE` is the wire value for 퇴원 후 첫 월요일: the scheduling engine
 * (`ProScheduleResolver`) resolves it as the first Monday strictly after the discharge date,
 * which is exactly what the requirement document specifies for these three cycles.
 *
 * The abolished `DAY_AFTER_DISCHARGE` is returned for no cycle. It survives in the type
 * because already-saved rows still carry it and the backend still resolves those.
 */
export function firstExecutionKindForCycle(
  cycle: ProMeasurementCycle,
): FirstExecutionKind {
  return cycle === 'DAILY' || cycle === 'QUARTERLY'
    ? 'N_DAYS_AFTER'
    : 'WEEK_AFTER_DISCHARGE';
}

/**
 * The N seeded when the cycle changes, or `null` for the cycles that hide the field.
 * The two numbers are the customer's, not incidental: 1 starts a 매일 item the day after
 * discharge, and 90 is the quarter itself.
 */
export function defaultNDaysForCycle(cycle: ProMeasurementCycle): number | null {
  if (cycle === 'DAILY') return 1;
  if (cycle === 'QUARTERLY') return 90;
  return null;
}

/**
 * The first-execution criteria an item form opens with. The stored kind is not copied: a row saved before
 * the criteria became derived can still carry a kind its cycle no longer allows, the screen shows the
 * derived one, and the server refuses to save the stored one back. So the kind is derived from the cycle,
 * as on a cycle change, and the stored value supplies N only where N keeps meaning the same day:
 *   - a stored `N_DAYS_AFTER` keeps its own N;
 *   - the retired `DAY_AFTER_DISCHARGE` (the day after discharge) becomes N = 1, the same date;
 *   - anything else takes the cycle's default N, or `fallbackNDays` for a cycle that hides N.
 */
export function initialFirstExecution(
  cycle: ProMeasurementCycle,
  stored: FirstExecution | null | undefined,
  fallbackNDays: number,
): { kind: FirstExecutionKind; nDays: number } {
  const kind = firstExecutionKindForCycle(cycle);
  if (stored?.kind === 'N_DAYS_AFTER' && stored.nDays != null) {
    return { kind, nDays: stored.nDays };
  }
  if (stored?.kind === 'DAY_AFTER_DISCHARGE') {
    return { kind, nDays: 1 };
  }
  return { kind, nDays: defaultNDaysForCycle(cycle) ?? fallbackNDays };
}

/**
 * Whether the 「퇴원 후 N일」 input is shown. Derived from the mapping above rather than
 * tested separately, so the field cannot appear for a cycle whose criteria carries no N —
 * two independent tests would drift the moment a cycle is added.
 */
export function cycleExposesNDays(cycle: ProMeasurementCycle): boolean {
  return firstExecutionKindForCycle(cycle) === 'N_DAYS_AFTER';
}

export function requiresDaysOfWeek(cycle: ProMeasurementCycle): boolean {
  return cycle === 'WEEKLY' || cycle === 'BIWEEKLY' || cycle === 'MONTHLY';
}

// PRD §3.2 / §3.3 / §8 — fields required to PUBLISH. Save Draft skips this.
export function validateItemForPublish(form: ValidatableForm): ValidationKeys {
  const out: ValidationKeys = {};

  if (!form.name.trim()) out.name = 'nameRequired';
  // PRD §8.1 (QC PID-054) — item name is capped at 200 chars.
  else if (form.name.length > 200) out.name = 'nameTooLong';
  if (form.scope === 'DISEASE_GROUP' && !form.diseaseGroupId) {
    out.diseaseGroupId = 'diseaseGroupRequired';
  }
  if (form.scope === 'DISEASE_SPECIFIC' && !form.diseaseCodeId) {
    out.diseaseCodeId = 'diseaseCodeRequired';
  }
  if (requiresDaysOfWeek(form.cycle) && form.daysOfWeek.length === 0) {
    out.daysOfWeek = 'daysRequired';
  }
  // PRD §8.1 (QC PID-055) — 'N days after discharge' requires 1..365.
  if (
    form.firstExecutionKind === 'N_DAYS_AFTER' &&
    (form.firstExecutionNDays < 1 || form.firstExecutionNDays > 365)
  ) {
    out['schedule.firstExecution.nDays'] = 'nDaysRange';
  }

  // PRD §3.3 / §8.2 — sum mode requires strictly monotonic ranges. A grade bound
  // left at its zero default counts as MISSING (QC PID-057); otherwise a non-
  // increasing pair is an OVERLAP (QC PID-056). The flat-zero default for new
  // items intentionally fails here so users set a real range before publishing.
  if (form.gradeSumG1Max <= 0 || form.gradeSumG2Max <= 0) {
    out['gradeSum.g1Max'] = 'gradeRangeMissing';
  } else if (form.gradeSumG2Max <= form.gradeSumG1Max) {
    out['gradeSum.g1Max'] = 'gradeRangeOverlap';
  }

  // PRD §3.3 / §8.1 (v1.8, Req Clarification Q75): management-guideline linkage is
  // NOT a publish prerequisite. A grade with no linked guideline must still save
  // and publish, so no guideline-required check runs here (guideline is read-only
  // and linked from PRD 07's Management Guideline screen).

  return out;
}
